// Small chart primitives for the Infra workspace, drawn with MUI boxes so they
// share the v2 tokens: thin bars on hairline tracks, 2px surface gaps between
// segments, a hover tooltip on every mark, text in ink tokens, never series color.

import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { getColors, getElevation, PaletteMode, rounded } from '../../theme/tokens';
import { reduceMotionSx, transition } from '../../theme/motion';
import { Lag, LAG_ORDER } from '../../lib/fleetModules';

// ---------------------------------------------------------------------------
// Color
// ---------------------------------------------------------------------------

/** Lag reads as a severity ladder; always shown with its label. */
export const LAG_HUES: Record<PaletteMode, Record<Lag, string>> = {
  light: { current: '#1aae39', patch: '#62aef0', minor: '#dd5b00', major: '#d93a3a', unknown: '#a39e98' },
  dark: { current: '#1aae39', patch: '#62aef0', minor: '#e8731f', major: '#e66767', unknown: '#6f6e69' },
};

export const LAG_LABELS: Record<Lag, string> = {
  current: 'On latest',
  patch: 'Patch behind',
  minor: 'Minor behind',
  major: 'Major behind',
  unknown: 'Unparsed',
};

/**
 * Ordinal blue ramp for a module's versions, newest first. Newest is the
 * strongest step (darkest on light, brightest on dark); past the ramp's
 * length the oldest versions share its last step.
 */
const VERSION_RAMP: Record<PaletteMode, string[]> = {
  light: ['#104281', '#1c5cab', '#2a78d6', '#5598e7', '#86b6ef'],
  dark: ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf'],
};

export const versionHue = (mode: PaletteMode, index: number): string => {
  const ramp = VERSION_RAMP[mode];
  return ramp[Math.min(index, ramp.length - 1)];
};

// ---------------------------------------------------------------------------
// Frames
// ---------------------------------------------------------------------------

export const ChartCard: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, actions, children }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: `${rounded.lg}px`, borderColor: c.hairline, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.98rem', color: c.ink }}>{title}</Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: c.inkMuted, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {actions}
      </Box>
      {children}
    </Paper>
  );
};

export const StatTile: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  to?: string;
}> = ({ label, value, hint, to }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const link = to ? { component: RouterLink, to } : {};
  return (
    <Paper
      variant="outlined"
      {...(link as object)}
      sx={{
        p: 2,
        borderRadius: `${rounded.lg}px`,
        borderColor: c.hairline,
        textDecoration: 'none',
        display: 'block',
        transition: transition(['border-color', 'box-shadow', 'transform']),
        ...(to && {
          '&:hover': {
            borderColor: c.inkFaint,
            transform: 'translateY(-2px)',
            boxShadow: getElevation(theme.palette.mode).soft,
          },
        }),
        ...reduceMotionSx,
      }}
    >
      <Typography
        sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: c.inkMuted }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.5px', color: c.ink }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: c.inkFaint }}>
          {hint}
        </Typography>
      )}
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Segmented bar (one stacked bar: a distribution of parts)
// ---------------------------------------------------------------------------

export interface Segment {
  key: string;
  label: string;
  value: number;
  color: string;
  tooltip?: React.ReactNode;
}

export const SegmentBar: React.FC<{ segments: Segment[]; height?: number; total?: number }> = ({
  segments,
  height = 10,
  total,
}) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const sum = total ?? segments.reduce((s, x) => s + x.value, 0);
  const visible = segments.filter(s => s.value > 0);
  return (
    <Box
      role="img"
      aria-label={visible.map(s => `${s.label}: ${s.value}`).join(', ')}
      sx={{ display: 'flex', gap: '2px', height, width: '100%', borderRadius: `${rounded.xs}px`, overflow: 'hidden', backgroundColor: c.hoverWash }}
    >
      {visible.map(s => (
        <Tooltip
          key={s.key}
          arrow
          placement="top"
          title={
            s.tooltip || (
              <span>
                {s.label}: <b>{s.value}</b>
                {sum > 0 && ` (${Math.round((s.value / sum) * 100)}%)`}
              </span>
            )
          }
        >
          <Box
            sx={{
              flexGrow: s.value,
              flexBasis: 0,
              minWidth: 3,
              backgroundColor: s.color,
              transition: transition('opacity'),
              '&:hover': { opacity: 0.8 },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

export const Legend: React.FC<{ items: Array<{ label: string; color: string; value?: React.ReactNode }> }> = ({
  items,
}) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mt: 1.25 }}>
      {items.map(i => (
        <Box key={i.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: i.color, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: c.inkSecondary }}>
            {i.label}
            {i.value !== undefined && (
              <Box component="span" sx={{ color: c.inkMuted, ml: 0.5 }}>
                {i.value}
              </Box>
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/** The fleet's lag distribution as one bar with a labelled legend. */
export const LagBar: React.FC<{ counts: Record<Lag, number>; height?: number; legend?: boolean }> = ({
  counts,
  height = 14,
  legend = true,
}) => {
  const theme = useTheme();
  const hues = LAG_HUES[theme.palette.mode];
  const total = LAG_ORDER.reduce((s, l) => s + counts[l], 0);
  const lags = LAG_ORDER.filter(l => l !== 'unknown' || counts.unknown > 0);
  return (
    <Box>
      <SegmentBar
        height={height}
        total={total}
        segments={lags.map(l => ({ key: l, label: LAG_LABELS[l], value: counts[l], color: hues[l] }))}
      />
      {legend && (
        <Legend
          items={lags.map(l => ({
            label: LAG_LABELS[l],
            color: hues[l],
            value: total > 0 ? `${counts[l]} · ${Math.round((counts[l] / total) * 100)}%` : '0',
          }))}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Bar list (ranked horizontal bars)
// ---------------------------------------------------------------------------

export interface BarRow {
  key: string;
  label: string;
  sublabel?: string;
  value: number;
  to?: string;
  /** Replaces the single bar with a distribution, still scaled to `value`. */
  segments?: Segment[];
  tooltip?: React.ReactNode;
}

export const BarList: React.FC<{
  rows: BarRow[];
  /** Formats the right-hand value. */
  format?: (v: number) => string;
  labelWidth?: number;
}> = ({ rows, format = v => String(v), labelWidth = 220 }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const max = Math.max(1, ...rows.map(r => r.value));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {rows.map(r => {
        const link = r.to ? { component: RouterLink, to: r.to } : {};
        return (
          <Box
            key={r.key}
            {...(link as object)}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 48px', sm: `${labelWidth}px 1fr 48px` },
              alignItems: 'center',
              columnGap: 1.5,
              py: 0.6,
              px: 0.75,
              mx: -0.75,
              borderRadius: `${rounded.sm}px`,
              textDecoration: 'none',
              color: 'inherit',
              ...(r.to && { '&:hover': { backgroundColor: c.hoverWash } }),
            }}
          >
            <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
              <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 500, color: c.ink }} title={r.label}>
                {r.label}
              </Typography>
              {r.sublabel && (
                <Typography noWrap variant="caption" sx={{ color: c.inkFaint, display: 'block', lineHeight: 1.2 }}>
                  {r.sublabel}
                </Typography>
              )}
            </Box>
            <Tooltip arrow placement="top" title={r.tooltip || ''} disableHoverListener={!r.tooltip || !!r.segments}>
              <Box sx={{ width: `${(r.value / max) * 100}%`, minWidth: 4 }}>
                {r.segments ? (
                  <SegmentBar segments={r.segments} height={10} />
                ) : (
                  <Box sx={{ height: 10, borderRadius: `0 ${rounded.xs}px ${rounded.xs}px 0`, backgroundColor: c.primary }} />
                )}
              </Box>
            </Tooltip>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: c.inkSecondary, textAlign: 'right' }}>
              {format(r.value)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * The progress line shown while jobs are still answering, then while Spacelift
 * is asked for latest releases, and the fallback when it can't be.
 */
export const FleetLoadNote: React.FC<{
  loaded: number;
  total: number;
  loading: boolean;
  registry?: { loading: boolean; error?: string };
}> = ({ loaded, total, loading, registry }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  let note: string | null = null;
  if (loading) {
    note =
      total === 0
        ? 'Loading the job catalog…'
        : `Reading module inventories: ${loaded} of ${total} terraform jobs. Charts fill in as they arrive.`;
  } else if (registry?.loading) {
    note = 'Checking Spacelift for each module’s latest release…';
  } else if (registry?.error) {
    note = `Spacelift lookup unavailable (${registry.error}). “Latest” falls back to the newest version deployed.`;
  }
  if (!note) return null;
  return (
    <Typography variant="body2" sx={{ color: c.inkMuted, mb: 2 }}>
      {note}
    </Typography>
  );
};
